import type { Widget } from '@/lib/manifest/types';
import { manifestWidgets } from './manifest-widgets';

// Widgets live in the agent's full manifest, which is published to the agent
// service's DDC bucket by `cef push` — NOT the card-only marketplace endpoint.
// This reads it the same way ROC does: resolve the numeric bucketId from the
// agent-service pubkey, then GET the manifest leaf off the public DDC content
// gateway. Every failure path returns [] (silent default) so the dashboard's
// "no widgets" state keeps working. See resolveWidgetUrl for the dag→URL step.

export interface FetchAgentWidgetsDeps {
  /** Orchestrator base, e.g. https://orchestrator.compute.dev.ddcdragon.com */
  orchestratorUrl: string;
  /** DDC content gateway base, e.g. https://cdn.devnet.ddc-dragon.com */
  cdnUrl: string;
  /** Agent service pubkey (AgentConnection.asPubKey). */
  asPubKey: string;
  /** Agent alias — the part after `:` in `<pubkey>:<alias>`. */
  alias: string;
  /** Connected version, e.g. `0.6.5`. */
  version: string;
}

function trimSlashes(s: string): string {
  return s.replace(/\/+$/, '');
}

/** Resolve the agent service's numeric DDC bucket id, or null when unknown. */
async function resolveBucketId(orchestratorUrl: string, asPubKey: string): Promise<string | null> {
  const url = `${trimSlashes(orchestratorUrl)}/api/v1/agent-services/${encodeURIComponent(asPubKey)}`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) return null;
  const body = (await res.json()) as { bucketId?: unknown };
  const id = body.bucketId;
  return typeof id === 'string' && id ? id : typeof id === 'number' ? String(id) : null;
}

export async function fetchAgentWidgets(deps: FetchAgentWidgetsDeps): Promise<Widget[]> {
  const { orchestratorUrl, cdnUrl, asPubKey, alias, version } = deps;
  if (!asPubKey || !alias || !version) return [];
  try {
    const bucketId = await resolveBucketId(orchestratorUrl, asPubKey);
    if (!bucketId) return [];
    const url = `${trimSlashes(cdnUrl)}/${encodeURIComponent(bucketId)}/agents/${encodeURIComponent(alias)}/${encodeURIComponent(version)}/manifest.json`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return [];
    return manifestWidgets(await res.json());
  } catch {
    return [];
  }
}
